import {
	ACCOUNT_API_GET_ACCOUNT,
	ACCOUNT_API_RESEND_VERIFICATION_EMAIL,
	SUBSCRIPTIONS_GET_BY_SUBSCRIBER,
} from 'apiRoutes';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import BackButton from 'components/common/BackButton/BackButton';
import SectionDivider from 'components/common/SectionDivider';
import { Table } from 'components/tables/Table';
import { mapProductRegistrations } from './ProductRegistrations/mapProductRegistrations';
import { productRegistrationTableColumns } from './ProductRegistrations/productRegistrationTableColumns';
import { createColumnHelper } from '@tanstack/react-table';
import HeadCell from 'components/tables/cells/HeadCell/HeadCell';
import {
	Box,
	Button,
	H1,
	HStack,
	IconAvatar,
	IconBlock,
	IconLockOpen,
	Link,
	VStack,
	IconCheckCircle,
	Tooltip,
} from '@montel/montelpro-shared-components-v2';
import { LabelAndValue } from 'components/common/LabelAndValue/LabelAndValue';
import { useAuth } from '@montel/montelpro-auth';
import { Email as EmailComponent } from 'components/common/Email/Email';
import { ProductAccessButtonCell } from 'components/common/Product/Cells/ProductAccessButtonCell';
import { EditEmailModal } from 'components/common/Email/EditEmailModal';
import { AccessControl } from 'components/common/AccessControl/AccessControl';
import { AdminRole } from 'domain/account/enums';
import { ProductRegistration } from 'domain/product/interfaces';
import { Email, MontelAccount } from 'domain/account/interfaces';
import { ProductAccessStatus } from 'domain/product/enums';
import SubscriptionTable from 'components/subscription/SubscriptionTable';
import { AccountAuditLog } from './AccountAuditLog';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { Subscription } from 'domain/subscription/interfaces/Subscription';
import { RequestLoader } from 'components/common/Loader';
import styled from 'styled-components';
import { AssignToCompanyModal } from './AssignToCompanyModal/AssignToCompanyModal';
import AssignToCompanyFormContextProvider from 'components/companies/AssignToCompanyForm/AssignToCompanyFormContext';
import { subscriptionTableColumns } from 'components/subscription/subscriptionTableColumns';
import { HubSpotLabel } from './HubspotLabel';
import { ManageHubSpotConnectionModal } from './ManageHubSpotConnectionModal';
import { AccountStatus } from 'components/common/AccountStatus/AccountStatus';
import { BlockAccountModal } from 'components/account/AccountsList/BlockAccountModal';
import { ReopenAccountModal } from 'components/account/AccountsList/ReopenAccountModal/ReopenAccountModal';
import useSubmit, { POST } from 'hooks/useSubmit';
import { RequestStatus } from 'types/RequestStatus';
import { NotFound } from 'components/common/NotFound/NotFound';
import useAuthRoles from 'hooks/useAuthRoles';
import { ConditionallyDisabled } from 'components/common/ConditionallyDisabled/ConditionallyDisabled';
import AccountPageContextProvider from './AccountPageContext';

const AccountCompany = styled(HStack)`
	min-width: 30%;
`;

const AccountProfilePage: FunctionComponent = () => {
	const { id } = useParams();

	const [productRegistrations, setProductRegistrations] = useState<
		ProductRegistration[]
	>([]);
	const [showEditEmailModal, setShowEditEmailModal] =
		useState<boolean>(false);
	const [showEditCompanyModal, setShowEditCompanyModal] =
		useState<boolean>(false);
	const [updatedAccountEmail, setUpdatedAccountEmail] = useState<Email>();
	// Small hack to force the audit log to remount when product access is changed, triggering it's useGetOnMount
	const [forceRefreshAuditLog, setForceRefreshAuditLog] = useState(0);
	const [showBlockAccountModal, setShowBlockAccountModal] = useState(false);
	const [showReopenAccountModal, setShowReopenAccountModal] = useState(false);
	const [showManageHubSpotConnectModal, setShowManageHubSpotConnectionModal] =
		useState<boolean>(false);

	const {
		data: account,
		status: accountStatus,
		getData: refreshAccountData,
		error,
	} = useGetOnMount<MontelAccount>(
		`${ACCOUNT_API_GET_ACCOUNT}?AccountId=${id}`,
	);

	const { data: subscriptions, status: subscriptionsStatus } = useGetOnMount<
		Subscription[]
	>(`${SUBSCRIPTIONS_GET_BY_SUBSCRIBER}?subscriberId=${id}`);

	const [resendVerificationEmail, resendVerificationEmailStatus] = useSubmit(
		`${ACCOUNT_API_RESEND_VERIFICATION_EMAIL}`,
		POST,
		{
			messageOnError: 'An error occured, please try again!',
			messageOnSuccess: `Verification email was resent to ${account?.email.value}`,
			showApiError: true,
		},
	);

	const subscriptionColumns = useMemo(
		() => [
			subscriptionTableColumns.application(),
			subscriptionTableColumns.plan(),
			subscriptionTableColumns.status(),
			subscriptionTableColumns.startDate(),
			subscriptionTableColumns.endDate(),
			subscriptionTableColumns.type(),
			subscriptionTableColumns.description(),
			subscriptionTableColumns.owner(),
			subscriptionTableColumns.expandedIndicator(),
		],
		[subscriptions],
	);

	const { user } = useAuth();
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();

	useEffect(() => {
		if (account) {
			setProductRegistrations(
				mapProductRegistrations(account.productRegistrations),
			);
		}
	}, [account]);

	const onProductAccessStatusChanged = (
		productId: string,
		newStatus: ProductAccessStatus,
	) => {
		const updatedProductRegistrations = productRegistrations.map(
			(productRegistration) => {
				if (productRegistration.productId === productId) {
					return {
						...productRegistration,
						accessStatus: newStatus,
						handledBy: {
							userName: `${user?.firstName} ${user?.lastName}`,
						},
					};
				}
				return productRegistration;
			},
		);
		setProductRegistrations(updatedProductRegistrations);
		setForceRefreshAuditLog(forceRefreshAuditLog + 1);
	};

	const onEmailChanged = (newEmail: Email) => {
		setShowEditEmailModal(false);
		setUpdatedAccountEmail(newEmail);
		setForceRefreshAuditLog(forceRefreshAuditLog + 1);
	};

	const onCompanyChanged = () => {
		setShowEditCompanyModal(false);
		refreshAccountData();
		setForceRefreshAuditLog(forceRefreshAuditLog + 1);
	};

	const onAccountLocked = () => {
		setShowBlockAccountModal(false);
		refreshAccountData();
		setForceRefreshAuditLog(forceRefreshAuditLog + 1);
	};

	const onAccountReopened = () => {
		setShowReopenAccountModal(false);
		refreshAccountData();
		setForceRefreshAuditLog(forceRefreshAuditLog + 1);
	};

	const onHubSpotChanged = () => {
		refreshAccountData();
		setForceRefreshAuditLog(forceRefreshAuditLog + 1);
	};

	const navigate = useNavigate();

	const columns = useMemo(
		() => [
			productRegistrationTableColumns.productRegistration(account?.id),
			productRegistrationTableColumns.accessStatus(),
			productRegistrationTableColumns.handledByName(),
			productRegistrationTableColumns.dateRegistered(),
			productRegistrationTableColumns.lastLogin(),
			productRegistrationTableColumns.lastActive(),
			createColumnHelper<ProductRegistration>().display({
				id: 'actions',
				enableSorting: false,
				meta: { width: '120px' },
				header: () => <HeadCell text="" />,
				cell: (info) => (
					<ProductAccessButtonCell
						productRegistration={info.row.original}
						userId={account?.id}
						onSuccess={onProductAccessStatusChanged}
					/>
				),
			}),
		],
		[productRegistrations, account],
	);

	if (accountStatus === RequestStatus.NOT_FOUND) {
		return (
			<NotFound title="Account Not Found" error={error}>
				<AccountAuditLog accountId={id as string} />
			</NotFound>
		);
	}

	const isAddSubscriptionDisabled =
		isRestrictedToRole(AdminRole.SALES) && !account?.company.id;

	return (
		<RequestLoader h="800px" status={accountStatus}>
			<Box bg="bgMainContainer">
				<BackButton />
				{account && (
					<AccountPageContextProvider
						account={account}
						refreshAccountData={refreshAccountData}
					>
						<Box bg="bgMainContainer">
							<VStack gap="xl">
								<HStack fullWidth justify="space-between">
									<VStack>
										<HStack align="center" gap="md">
											<IconAvatar boxSize="1.5rem" />
											<H1>{`${account.firstName} ${account.lastName}`}</H1>
										</HStack>
										<HStack align="center">
											<HubSpotLabel
												isPrimary={account.isPrimary}
												hubSpotId={account.hubSpotId}
											/>
											<AccessControl
												allowedRoles={[
													AdminRole.MONTEL_ACCOUNT_ADMIN,
													AdminRole.SUPER_ADMIN,
												]}
											>
												<Button
													variant="tertiary"
													text="Edit"
													size="sm"
													onClick={() =>
														setShowManageHubSpotConnectionModal(
															true,
														)
													}
												/>
											</AccessControl>
										</HStack>
									</VStack>
									<HStack>
										<AccessControl
											allowedRoles={[
												AdminRole.SUPER_ADMIN,
											]}
										>
											{!account.locked ? (
												<Button
													variant="negative"
													text="Block account"
													size="md"
													leftIcon={<IconBlock />}
													onClick={() =>
														setShowBlockAccountModal(
															true,
														)
													}
												/>
											) : (
												<Button
													variant="primary"
													text="Reopen account"
													size="md"
													leftIcon={<IconLockOpen />}
													onClick={() =>
														setShowReopenAccountModal(
															true,
														)
													}
												/>
											)}
										</AccessControl>
										<AccessControl
											allowedRoles={[
												AdminRole.SUPER_ADMIN,
												AdminRole.MONTEL_ACCOUNT_ADMIN,
											]}
										>
											{!account.email?.verified && (
												<Button
													variant="secondary"
													text="Resend verification email"
													size="md"
													onClick={() =>
														resendVerificationEmail(
															{
																accountId:
																	account.id,
															},
														)
													}
													isLoading={
														resendVerificationEmailStatus ===
														RequestStatus.LOADING
													}
												/>
											)}
										</AccessControl>
									</HStack>
								</HStack>
								<HStack fullWidth gap="xl">
									<LabelAndValue
										minWidth="30%"
										label="Email"
										value={
											<HStack align="center">
												<EmailComponent
													email={
														updatedAccountEmail
															? updatedAccountEmail
															: account.email
													}
													fontSize="sm"
												/>
												<AccessControl
													allowedRoles={[
														AdminRole.SUPER_ADMIN,
														AdminRole.MONTEL_ACCOUNT_ADMIN,
														AdminRole.PRODUCT_ADMIN_ALERTS,
														AdminRole.PRODUCT_ADMIN_ENAPPSYS,
														AdminRole.PRODUCT_ADMIN_EQ,
														AdminRole.PRODUCT_ADMIN_MATCH,
														AdminRole.PRODUCT_ADMIN_MARKETS,
														AdminRole.PRODUCT_ADMIN_NEWS_PAGE,
														AdminRole.PRODUCT_ADMIN_SYSPOWER,
														AdminRole.PRODUCT_ADMIN_DATAFABRICATOR,
														AdminRole.PRODUCT_ADMIN_MONTELPPA,
													]}
												>
													<Button
														variant="tertiary"
														text="Edit"
														size="sm"
														onClick={() =>
															setShowEditEmailModal(
																true,
															)
														}
													/>
												</AccessControl>
											</HStack>
										}
									/>
									<LabelAndValue
										minWidth="30%"
										label="Username"
										value={account.username || 'N/A'}
									/>
									<LabelAndValue
										minWidth="30%"
										label="Montel account status"
										value={
											<AccountStatus
												locked={account.locked}
												hasPendingInvitation={
													account.hasPendingInvitation
												}
											/>
										}
									/>
								</HStack>
								<HStack fullWidth gap="xl">
									<CompanyComponent
										account={account}
										setShowEditCompanyModal={
											setShowEditCompanyModal
										}
									/>
									<LabelAndValue
										minWidth="30%"
										label="Telephone number"
										value={account.phoneNumber}
									/>
									<LabelAndValue
										minWidth="30%"
										label="Region"
										value={account.country.name}
									/>
								</HStack>
								<HStack fullWidth gap="xl">
									<LabelAndValue
										minWidth="30%"
										label="Job title"
										value={account.company.jobTitle}
									/>
								</HStack>
							</VStack>
							<SectionDivider />
							<SubscriptionTable
								columns={subscriptionColumns}
								requestStatus={subscriptionsStatus}
								subscriptions={subscriptions}
							/>
							<HStack fullWidth justify="flex-start">
								<ConditionallyDisabled
									disableIf={isAddSubscriptionDisabled}
									tooltip="You may only add a subscription to a user who is connected to a company"
								>
									<Button
										text="Add subscription"
										onClick={() =>
											navigate(
												`/subscriptions/create?accountId=${account.id}&companyId=${account.company.id}`,
											)
										}
									/>
								</ConditionallyDisabled>
							</HStack>
							<SectionDivider />
							<Table<ProductRegistration>
								header="Applications"
								containerGap="xs"
								columns={columns}
								data={productRegistrations}
								initialSorting={[
									{
										id: 'dateRegistered',
										desc: true,
									},
								]}
							/>
							<SectionDivider />
							<AccountAuditLog
								accountId={account.id}
								key={forceRefreshAuditLog}
							/>
						</Box>
					</AccountPageContextProvider>
				)}
			</Box>
			<EditEmailModal
				account={account}
				showModal={showEditEmailModal}
				setShowModal={setShowEditEmailModal}
				onSuccess={onEmailChanged}
			/>
			<BlockAccountModal
				user={account}
				onSuccess={onAccountLocked}
				setModalOpen={setShowBlockAccountModal}
				isOpen={showBlockAccountModal}
			/>
			<ReopenAccountModal
				user={account}
				onSuccess={onAccountReopened}
				setModalOpen={setShowReopenAccountModal}
				isOpen={showReopenAccountModal}
			/>
			<AssignToCompanyFormContextProvider
				account={account}
				subscriptions={subscriptions}
			>
				<AssignToCompanyModal
					account={account}
					showModal={showEditCompanyModal}
					setShowModal={setShowEditCompanyModal}
					onSuccess={onCompanyChanged}
				/>
			</AssignToCompanyFormContextProvider>
			<ManageHubSpotConnectionModal
				account={account}
				showModal={showManageHubSpotConnectModal}
				setShowModal={setShowManageHubSpotConnectionModal}
				onSuccess={onHubSpotChanged}
			/>
		</RequestLoader>
	);
};

const CompanyComponent: React.FC<{
	account: MontelAccount;
	setShowEditCompanyModal: (show: true) => void;
}> = ({ account, setShowEditCompanyModal }) => {
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();
	return (
		<AccountCompany align="center">
			<LabelAndValue
				minWidth="30%"
				label="Company"
				value={
					<HStack align="center">
						{account.company.id ? (
							<HStack gap="0px" align="center">
								<Link href={'/companies/' + account.company.id}>
									{account.company.name}
								</Link>
								{account.company.isCustomer && (
									<Tooltip
										contentSize="sm"
										component={
											<IconCheckCircle
												size="sm"
												color="iconSuccess"
											/>
										}
									>
										This company is a customer
									</Tooltip>
								)}
							</HStack>
						) : (
							<HStack align="center">
								<span>{account.company.name}</span>
							</HStack>
						)}
						<AccessControl
							allowedRoles={[
								AdminRole.SUPER_ADMIN,
								AdminRole.MONTEL_ACCOUNT_ADMIN,
							]}
						>
							<Button
								variant="tertiary"
								text="Edit"
								size="sm"
								onClick={() => setShowEditCompanyModal(true)}
								isDisabled={isRestrictedToRole(AdminRole.SALES)}
							/>
						</AccessControl>
					</HStack>
				}
			/>
		</AccountCompany>
	);
};

export default AccountProfilePage;
